import {
  AccessoriesIcon,
  CheckboxLabelled,
  CosmeticsIcon,
  FunctionalIcon,
  InputMessage,
  MechanicalIcon,
  SegmentedControl,
  typographyRem,
} from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import { isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'

import { useQuote } from '../../../../hooks/useQuote'
import { useCarPageContext } from '../../../../pages/CarPageProvider'
import { usePreQuoteContext } from '../../../../pages/PageValidator'
import Accordion from '../../../shared/AccordionV2'
import { ToolTip } from '../../../ToolTip'
import { GenericSectionStyle } from '../../GenericSectionStyle'
import { CarModificationsStyle } from './CarModificationsStyle'
import { CategoryPickerAccordion } from './CategoryPickerAccordion'
const { h4: H4, h5: H5, paragraphBody: ParagraphBody } = typographyRem

export const CarModifications = () => {
  const quote = useQuote()
  const { updatedCarDetails, setUpdatedCarDetails, modificationsData } = useCarPageContext()
  const { validInputs, showErrorBanner, notAnsweredMessage, updateValidInputs } = usePreQuoteContext()

  const [hasModifications, setHasModifications] = useState<boolean | undefined>(
    typeof quote?.details?.vehicle?.has_modifications === 'boolean'
      ? quote?.details?.vehicle?.has_modifications
      : undefined,
  )
  const [modifications, setModifications] = useState<string[] | undefined>()
  const [accessories, setAccessories] = useState<string[]>([])
  const [cosmetics, setCosmetics] = useState<string[]>([])
  const [mechanical, setMechanical] = useState<string[]>([])
  const [functional, setFunctional] = useState<string[]>([])
  const [other, setOther] = useState<string[]>([])

  const handleHasModifications = (value: boolean) => {
    setHasModifications(value)
    DataLayer.customEvent({
      eventName: `modifications ${value ? 'yes' : 'no'}`,
      section: 'car',
      eventType: 'form tracking',
    })
    // Category picker and Checkbox labelled states can't be controlled/ updated by the parent without changes
    // Decision to leave functionality as it is now where modifications aren't reset when toggling hasModifications on the UI
    setUpdatedCarDetails({
      ...updatedCarDetails,
      has_modifications: value,
      modifications:
        value && modifications
          ? modifications.map(value => {
              return { code: value }
            })
          : [],
    })
  }

  const handlePreSelected = (modifications: string[], sectionMods: any) => {
    if (modifications?.length < 1) return []
    return modifications.filter((value: string) => {
      if (sectionMods.find((mod: any) => mod?.value === value)) {
        return value
      }
      return ''
    })
  }

  useEffect(() => {
    if (modificationsData) {
      const { modifications } = modificationsData
      const stringMods = quote?.details?.vehicle?.modifications?.map((item: any) => item.code) || []

      setAccessories(handlePreSelected(stringMods, modifications?.accessories))
      setCosmetics(handlePreSelected(stringMods, modifications?.cosmetics))
      setMechanical(handlePreSelected(stringMods, modifications?.mechanical))
      setFunctional(handlePreSelected(stringMods, modifications?.functional))
      stringMods.includes('999') && setOther(['999'])
    }
  }, [modificationsData])

  useEffect(() => {
    if (modificationsData) {
      const updatedModifications = [...accessories, ...cosmetics, ...mechanical, ...functional, ...other]
      setModifications(updatedModifications)
      if (
        JSON.stringify(updatedModifications) !==
        JSON.stringify(quote?.details?.vehicle?.modifications?.map((item: any) => item.code))
      ) {
        setUpdatedCarDetails({
          ...updatedCarDetails,
          modifications: updatedModifications.map(value => {
            return { code: value }
          }),
        })
      }
    }
  }, [accessories, cosmetics, mechanical, functional, other])

  useEffect(() => {
    updateValidInputs({ field: 'hasModifications', value: typeof hasModifications === 'boolean' })
    updateValidInputs({
      field: 'modifications',
      value: !hasModifications || !isEmpty(modifications),
    })
  }, [hasModifications])
  useEffect(() => {
    let validModification = false
    if (
      !hasModifications ||
      (typeof hasModifications === 'boolean' && hasModifications && modifications && modifications?.length > 0)
    ) {
      validModification = true
    }
    updateValidInputs({
      field: 'modifications',
      value: validModification,
    })
  }, [modifications])

  return (
    <CarModificationsStyle>
      <H4>Car modifications.</H4>
      <GenericSectionStyle>
        <div className="generic-wrapper">
          <div className="question-container" id="hasModifications">
            <div className="question-text-container">
              <H5>Has your car been modified, adapted or fitted with any non-manufacturer optional extras?</H5>
              <ParagraphBody className="question-info">
                For example, have there been changes to the bodywork, engine or wheels?
              </ParagraphBody>
            </div>
            <div className="input-info-container">
              <SegmentedControl
                options={[
                  { label: 'yes', value: true },
                  { label: 'no', value: false },
                ]}
                name="has-modifications"
                preFilledValue={hasModifications}
                error={showErrorBanner && hasModifications === undefined ? notAnsweredMessage : ''}
                onClick={value => typeof value === 'boolean' && handleHasModifications(value)}
                dataCy="has-modifications-toggle"
                ariaLiveOnFocus
              />
              <ToolTip
                text="Tell us if you or any previous owners have made any modifications to the car that weren't fitted by the manufacturer. Modifications that increase security can potentially reduce your premium, while ones that make your car more attractive to thieves may increase your premium. "
                className="tool-tip tip-top-medium"
                dataCy="modifications-info-button"
              />
            </div>
          </div>
          {hasModifications && modifications && (
            <div className="question-container" id="modifications">
              <div className="question-text-container">
                <H5>Which modifications have you made?</H5>
                <div className="info-tip-container">
                  <ParagraphBody className="question-info">Check the boxes that apply.</ParagraphBody>
                  <ToolTip
                    className="mod-tool-tip tip-top-xs"
                    text="Please tick all non-manufacturer fitted modifications that have been made to your car so we can give you an accurate quote."
                  />
                </div>
              </div>
              <Accordion>
                <CategoryPickerAccordion
                  title="Accessories and add-ons"
                  sections={modificationsData?.modifications?.accessories || []}
                  onClick={(value: string[]) => setAccessories(value)}
                  preFilled={accessories}
                  icon={<AccessoriesIcon />}
                />
                <CategoryPickerAccordion
                  title="Cosmetics and body"
                  sections={modificationsData?.modifications?.cosmetics || []}
                  onClick={(value: string[]) => setCosmetics(value)}
                  preFilled={cosmetics}
                  icon={<CosmeticsIcon />}
                />
                <CategoryPickerAccordion
                  title="Mechanical and performance"
                  sections={modificationsData?.modifications?.mechanical || []}
                  onClick={(value: string[]) => setMechanical(value)}
                  preFilled={mechanical}
                  icon={<MechanicalIcon />}
                />
                <CategoryPickerAccordion
                  title="Functional changes"
                  sections={modificationsData?.modifications?.functional || []}
                  onClick={(value: string[]) => setFunctional(value)}
                  preFilled={functional}
                  icon={<FunctionalIcon />}
                />
              </Accordion>
              <div className="not-listed">
                <CheckboxLabelled
                  value="999"
                  label="Not listed above"
                  onClick={(value: '999') => setOther(other?.includes(value) ? [] : [value])}
                  preFilled={other?.length === 1}
                />
              </div>
              {showErrorBanner && !validInputs?.modifications && (
                <InputMessage
                  position="bottom"
                  message="Please tick your modifications to continue. If you can’t find yours, tick 'other'"
                  hideArrow
                  className="page-level-error"
                />
              )}
            </div>
          )}
        </div>
      </GenericSectionStyle>
    </CarModificationsStyle>
  )
}
